import { Button, Flex, Heading, Text, TextLink, Tooltip } from '@modusign/design-system';
import { useGetIntegrationAppInfo } from '@modusign/openapi-client-user';
import { ReactNode } from 'react';
import { useHistory } from 'react-router';

import { useSubscription } from '@app/features/subscription/hooks/subscription';
import SubscriptionStatus from '@app/features/subscription/types/SubscriptionStatus';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useOtherWorkspace, useWorkspace } from '@app/hooks/workspace';
import { PlanTitle } from '@app/libs/planTitle';
import workspaceService from '@app/libs/workspaceService';

interface Props {
  featurePlan?: PlanTitle;
  url: string;
  children: ReactNode;
}

export default function FeatureBlockViewContent({ featurePlan, url, children }: Props) {
  const history = useHistory();

  const { isPermitted: paymentIsPermitted } = useMemberPermissions('MANAGE_PAYMENT');
  const currentWorkspace = useWorkspace();
  const subscription = useSubscription();
  const integrationAppName = useGetIntegrationAppInfo(
    subscription.data?.plan?.applicationId ?? '',
    { query: { enabled: !!subscription.data?.plan?.applicationId } }
  ).data?.name;

  if (!currentWorkspace.active) {
    return (
      <Flex
        alignItems="center"
        bg="bodyNeutralDark"
        flexDirection="column"
        mb={[9, null, 0]}
        minHeight="calc(100vh - 62px)"
        minWidth={[0, null, 'md']}
        mx="auto"
        px={4}
        py={[8, null, '85px']}
      >
        <Heading fontSize="md" fontWeight="bold" mb={8}>
          공용 워크스페이스에서 이용해 주세요.
        </Heading>

        <GoToPublicWorkspaceButton />
      </Flex>
    );
  }

  if (subscription.data?.status !== SubscriptionStatus.ACTIVE) {
    return (
      <Flex
        alignItems="center"
        bg="bodyNeutralDark"
        flexDirection="column"
        mb={[9, null, 0]}
        minHeight="calc(100vh - 62px)"
        minWidth={[0, null, 'md']}
        mx="auto"
        px={4}
        py={[8, null, '85px']}
      >
        <Heading fontSize="md" fontWeight="bold">
          {featurePlan
            ? `${featurePlan} 요금제부터 이용하실 수 있습니다.`
            : '요금제 구매 후 이용하실 수 있습니다.'}
        </Heading>
        <Tooltip content="권한 없음" disabled={paymentIsPermitted}>
          <Button
            disabled={!paymentIsPermitted}
            mt={8}
            variant="primary"
            onClick={() => history.push('/payment/subscription')}
          >
            요금제 구매하기
          </Button>
        </Tooltip>
        {children}
        <TextLink href={url} mt={9} target="_blank">
          기능 이용 안내
        </TextLink>
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      bg="bodyNeutralDark"
      flexDirection="column"
      mb={[9, null, 0]}
      minHeight="calc(100vh - 62px)"
      minWidth={[0, null, 'md']}
      mx="auto"
      px={4}
      py={[8, null, '85px']}
    >
      <Heading fontSize="md" fontWeight="bold">
        {(() => {
          if (featurePlan) {
            return `${featurePlan} 요금제부터 이용하실 수 있습니다.`;
          }
          return '요금제에 기능 추가 후 이용하실 수 있습니다.';
        })()}
      </Heading>

      {(() => {
        if (integrationAppName) {
          return <Text mt={4}>{`요금제를 제공받은 '${integrationAppName}'에 문의해 주세요.`}</Text>;
        }
        if (subscription.data.plan?.isCustom) {
          return (
            <Tooltip content="권한 없음" disabled={paymentIsPermitted}>
              <Button
                className="tagContactButton"
                disabled={!paymentIsPermitted}
                mt={8}
                variant="primary"
              >
                요금제 상담받기
              </Button>
            </Tooltip>
          );
        }
        return (
          <Tooltip content="권한 없음" disabled={paymentIsPermitted}>
            <Button
              disabled={!paymentIsPermitted}
              mt={8}
              variant="primary"
              onClick={() => history.push('/payment/plan-change')}
            >
              요금제 업그레이드
            </Button>
          </Tooltip>
        );
      })()}
      {children}
      <TextLink href={url} mt={9} rel="noopener noreferrer" target="_blank">
        기능 이용 안내
      </TextLink>
    </Flex>
  );
}

function GoToPublicWorkspaceButton() {
  const otherWorkspace = useOtherWorkspace();

  if (!otherWorkspace) {
    return null;
  }

  return (
    <Button
      variant="subtle"
      onClick={() => {
        workspaceService.setWorkspaceId(otherWorkspace.id);
      }}
    >
      공용 워크스페이스로 가기
    </Button>
  );
}
